import { FC, SyntheticEvent, useMemo, useRef, useState } from "react";

import ExpandMore from "@mui/icons-material/ExpandMore";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItem from "@mui/material/ListItem";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ClearIcon } from "@mui/x-date-pickers";

import { AutocompleteOptionType } from "../../../models/autocomplete";
import { useZoneOptions } from "./useZoneOptions";

interface ZoneSelectProps {
  value: AutocompleteOptionType | null;
  onChange: (value: AutocompleteOptionType | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
}

const ZoneSelect: FC<ZoneSelectProps> = ({
  value,
  onChange,
  disabled = false,
  size = "small",
}) => {
  const hoveredRef = useRef<AutocompleteOptionType | null>(null);
  const [open, setOpen] = useState(false);

  const { zoneOptions, loading } = useZoneOptions();

  const selectedOption = useMemo((): AutocompleteOptionType | null => {
    if (!value || !value.value) {
      return null;
    }

    return zoneOptions.find(option => option?.value === value.value) ?? null;
  }, [value, zoneOptions]);

  const handleChange = (
    _: SyntheticEvent,
    newOption: AutocompleteOptionType | null
  ) => {
    if (!disabled) {
      onChange(newOption && newOption.value ? newOption : null);
    }
  };

  const handleClear = () => {
    if (!disabled) {
      onChange(null);
    }
  };

  const handleOnOpen = () => {
    setOpen(true);
  };

  const handleOnClose = () => {
    setOpen(false);

    hoveredRef.current = null;
  };

  const handleOnMouseEnter = (option: AutocompleteOptionType) => {
    hoveredRef.current = option;
  };

  const handleOnMouseLeave = () => {
    hoveredRef.current = null;
  };

  return (
    <Autocomplete
      fullWidth
      size={size}
      loading={loading}
      options={zoneOptions}
      getOptionLabel={(option: AutocompleteOptionType) => option?.title ?? ""}
      isOptionEqualToValue={(option, val) => option?.value === val?.value}
      value={selectedOption}
      clearIcon={null}
      onChange={handleChange}
      disabled={disabled || loading}
      popupIcon={<ExpandMore />}
      open={open}
      onOpen={handleOnOpen}
      onClose={handleOnClose}
      renderOption={(props, option) => (
        <ListItem
          {...props}
          onMouseEnter={() => handleOnMouseEnter(option)}
          onMouseLeave={handleOnMouseLeave}
          sx={{
            bgcolor:
              hoveredRef.current?.title === option?.title
                ? "inherit"
                : "transparent",
            "&.Mui-focused": { bgcolor: "transparent" },
            "&[aria-selected='true']": { bgcolor: "transparent" },
          }}
        >
          <Typography>{option?.title}</Typography>
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="All zones"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {value && value.value !== null && !disabled && (
                  <InputAdornment
                    position="end"
                    sx={{
                      cursor: "pointer",
                      position: "absolute",
                      right: "36px",
                    }}
                  >
                    <IconButton onClick={handleClear} size={size} sx={{ p: 0 }}>
                      <ClearIcon fontSize={size} />
                    </IconButton>
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PopperComponent={props => (
        <Popper
          {...props}
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        />
      )}
    />
  );
};

export default ZoneSelect;
